```vue
<template>
  <section class="service-page">

    <!-- HERO -->
    <section class="hero">

      <div class="hero-content">

        <span class="eyebrow">
          Dominio • Correos Corporativos • Infraestructura Digital
        </span>

        <h1 class="hero-title">
          Presencia Digital Profesional
        </h1>

        <p class="hero-description">
          Configuramos la infraestructura digital esencial que tu negocio necesita
          para verse profesional, generar confianza y operar correctamente desde el primer contacto.
        </p>

        <div class="hero-actions">

          <button
            class="primary-btn"
            type="button"
            :disabled="loading"
            @click="goToServiceForm"
          >
            {{ loading ? "Cargando..." : "Solicitar Configuración" }}
          </button>

          <div class="hero-badges">
            <span>Dominio Profesional</span>
            <span>Correos Corporativos</span>
            <span>Google Workspace</span>
            <span>Microsoft 365</span>
          </div>

        </div>

      </div>

      <div class="hero-image">

        <img
          src="/images/img16.jpg"
          alt="Infraestructura digital empresarial"
          loading="lazy"
          decoding="async"
        />

      </div>

    </section>

    <!-- PROBLEMS -->
    <section class="problems">

      <div class="section-heading">

        <span class="section-label">
          Problemas comunes
        </span>

        <h2>
          Lo que afecta la percepción de tu negocio
        </h2>

      </div>

      <div class="problem-grid">

        <article
          v-for="(problem, i) in problems"
          :key="i"
          class="problem-card"
        >

          <span class="problem-icon">
            ✕
          </span>

          <p>
            {{ problem }}
          </p>

        </article>

      </div>

    </section>

    <!-- SOLUTIONS -->
    <section class="solutions">

      <div class="section-heading">

        <span class="section-label">
          Soluciones incluidas
        </span>

        <h2>
          Infraestructura profesional para tu negocio
        </h2>

      </div>

      <div class="solution-grid">

        <article
          v-for="solution in solutions"
          :key="solution.title"
          class="solution-card"
        >

          <div class="solution-icon">
            {{ solution.icon }}
          </div>

          <h3>
            {{ solution.title }}
          </h3>

          <p>
            {{ solution.description }}
          </p>

          <ul>

            <li
              v-for="(item, i) in solution.features"
              :key="i"
            >
              ✓ {{ item }}
            </li>

          </ul>

        </article>

      </div>

    </section>

    <!-- MICRO SERVICES -->
    <section class="microservices">

      <div class="section-heading">

        <span class="section-label">
          Microservicios
        </span>

        <h2>
          Soluciones específicas
        </h2>

      </div>

      <div class="micro-grid">

        <article
          v-for="service in microservices"
          :key="service.title"
          class="micro-card"
        >

          <h3>
            {{ service.title }}
          </h3>

          <p>
            {{ service.description }}
          </p>

          <span class="price">
            {{ service.price }}
          </span>

        </article>

      </div>

    </section>

    <!-- BENEFITS -->
    <section class="benefits">

      <div class="section-heading center">

        <span class="section-label">
          Beneficios
        </span>

        <h2>
          Lo que obtiene tu negocio
        </h2>

      </div>

      <div class="benefit-grid">

        <article
          v-for="(benefit, i) in benefits"
          :key="i"
          class="benefit-card"
        >

          <span>
            ✓
          </span>

          <p>
            {{ benefit }}
          </p>

        </article>

      </div>

    </section>

    <!-- PROCESS -->
    <section class="process">

      <div class="section-heading center">

        <span class="section-label">
          Nuestro proceso
        </span>

        <h2>
          Cómo trabajamos
        </h2>

      </div>

      <div class="process-grid">

        <article
          v-for="step in process"
          :key="step.number"
          class="process-card"
        >

          <span class="step-number">
            {{ step.number }}
          </span>

          <h3>
            {{ step.title }}
          </h3>

          <p>
            {{ step.description }}
          </p>

        </article>

      </div>

    </section>

    <!-- CTA -->
    <section class="cta-section">

      <div class="cta-card">

        <span class="cta-label">
          Presencia • Profesionalismo • Confianza
        </span>

        <h2>
          Haz que tu negocio se vea profesional desde el primer contacto
        </h2>

        <p>
          Configuramos la base digital que toda empresa necesita para operar correctamente
          y generar confianza frente a clientes y proveedores.
        </p>

        <button
          class="primary-btn"
          type="button"
          :disabled="loading"
          @click="goToServiceForm"
        >
          {{ loading ? "Cargando..." : "Cotizar Infraestructura" }}
        </button>

      </div>

    </section>

  </section>
</template>

<script>
import { useHead } from "@vueuse/head";

export default {
  name: "ProfessionalDigitalPresence",

  data() {
    return {
      loading: false,

      problems: Object.freeze([
        "Uso de correos personales para clientes",
        "No tener dominio empresarial",
        "Falta de credibilidad profesional",
        "Problemas de entregabilidad de correos",
        "Infraestructura digital desorganizada",
        "Mala imagen frente a clientes y proveedores"
      ]),

      solutions: Object.freeze([
  {
    icon: "⚙️",
    title: "Operativo",
    description: "Todo lo que necesitas para existir y operar digitalmente de forma profesional.",
    features: [
      "Dominio .com o .mx",
      "Hosting y SSL",
      "Correos corporativos",
      "Landing Page o Sitio Web",
      "Google Workspace / Microsoft 365",
    ],
  },
  {
    icon: "📈",
    title: "Crecimiento",
    description: "Herramientas y procesos para que tu operación escale y genere resultados.",
    features: [
      "Google Analytics",
      "Dashboards básicos",
      "Automatizaciones",
      "Integraciones entre sistemas",
      "Optimización de procesos",
    ],
  },
  {
    icon: "🎯",
    title: "Consultoría",
    description: "Analizamos tu negocio y diseñamos la estrategia digital correcta antes de ejecutar.",
    features: [
      "Diagnóstico digital",
      "Estructura y organización documental",
      "Estandarización operativa",
      "Capacitación al equipo",
      "Acompañamiento estratégico",
    ],
  },
]),

      microservices: Object.freeze([
        {
          title: "Configuración de Dominio",
          description:
            "Dominio empresarial y configuración técnica.",
        },
        {
          title: "Correos Corporativos",
          description:
            "Implementación profesional de correos empresariales.",
        },
        {
          title: "Google Workspace",
          description:
            "Configuración y administración inicial.",
        },
        {
          title: "Microsoft 365",
          description:
            "Configuración empresarial profesional.",
        },
        {
          title: "SPF / DKIM / DMARC",
          description:
            "Protección y entregabilidad de correos.",
        },
        {
          title: "Migración de Correos",
          description:
            "Migración desde proveedores anteriores.",
        }
      ]),

      benefits: Object.freeze([
        "Mayor credibilidad profesional",
        "Mejor imagen empresarial",
        "Correos más seguros",
        "Mejor entregabilidad",
        "Infraestructura organizada",
        "Base lista para crecer digitalmente"
      ]),

      process: Object.freeze([
        {
          number: "01",
          title: "Diagnóstico",
          description:
            "Revisamos la infraestructura actual del negocio."
        },
        {
          number: "02",
          title: "Configuración",
          description:
            "Implementamos dominio, correos y herramientas."
        },
        {
          number: "03",
          title: "Validación",
          description:
            "Verificamos seguridad y funcionamiento."
        },
        {
          number: "04",
          title: "Capacitación",
          description:
            "Explicamos el uso correcto de la infraestructura."
        }
      ])
    };
  },

  setup() {
    useHead({
      title: "Presencia Digital Profesional | BearTech",
      htmlAttrs: {
        lang: "es-MX"
      },
      meta: [
        {
          name: "description",
          content:
            "Configuración de dominio empresarial, correos corporativos, Google Workspace y presencia digital profesional para negocios."
        }
      ]
    });
  },

  methods: {
    async goToServiceForm() {
      if (this.loading) return;

      this.loading = true;

      try {
        await this.$router.push("/servicios/presencia-profesional/formulario");
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>

/* ===== PAGE ENTRY ===== */

.service-page{
  opacity:0;
  transform:translateY(30px);

  animation:
    pageEnter .9s ease forwards;

  background:
    linear-gradient(
      180deg,
      #f8fafc 0%,
      #ffffff 100%
    );

  color:#0f172a;
}

@keyframes pageEnter{
  from{
    opacity:0;
    transform:translateY(30px);
  }

  to{
    opacity:1;
    transform:translateY(0);
  }
}

/* ===== GLOBAL ===== */

section{
  padding:5rem 1.5rem;
}

.section-heading{
  margin-bottom:3rem;

  opacity:0;
  transform:translateY(20px);

  animation:
    fadeUp .8s ease forwards;
}

.section-heading.center{
  text-align:center;
}

.section-label{
  display:inline-block;

  margin-bottom:1rem;
  padding:.45rem 1rem;

  border-radius:999px;

  background:#ccfbf1;
  color:#0f766e;

  font-size:.85rem;
  font-weight:700;
}

.section-heading h2{
  font-size:clamp(2rem,4vw,3rem);
  color:#012719;
}

/* ===== HERO ===== */

.hero{
  max-width:1300px;
  margin:auto;

  min-height:90vh;

  display:grid;
  grid-template-columns:1fr 1fr;
  gap:4rem;
  align-items:center;
}

.hero-content{
  opacity:0;
  transform:translateX(-40px);

  animation:
    fadeLeft 1s ease .2s forwards;
}

.hero-image{
  overflow:hidden;
  border-radius:30px;

  box-shadow:
    0 25px 60px rgba(0,0,0,.12);

  opacity:0;
  transform:translateX(40px) scale(.96);

  animation:
    fadeRight 1s ease .35s forwards;
}

.hero-image img{
  width:100%;
  height:750px;

  object-fit:cover;
  display:block;

  animation:
    imageZoom 8s ease-in-out infinite alternate;
}

.eyebrow{
  display:inline-block;
  margin-bottom:1.5rem;

  color:#0f766e;
  font-weight:700;
}

.hero-title{
  font-size:clamp(3rem,7vw,5rem);
  line-height:1;

  margin-bottom:1.5rem;

  color:#012719;
}

.hero-description{
  max-width:600px;

  font-size:1.15rem;
  line-height:1.9;

  color:#475569;
}

.hero-actions{
  margin-top:2rem;
}

.hero-badges{
  display:flex;
  flex-wrap:wrap;
  gap:.8rem;

  margin-top:1.5rem;
}

.hero-badges span{
  padding:.6rem 1rem;

  border-radius:999px;

  background:white;
  border:1px solid #dbe4ea;

  font-size:.9rem;

  transition:
    transform .25s ease,
    background .25s ease,
    border-color .25s ease;
}


/* ===== BUTTON ===== */

.primary-btn{
  position:relative;
  overflow:hidden;

  padding:1rem 2rem;

  border:none;
  border-radius:14px;

  background:
    linear-gradient(
      135deg,
      #051b19,
      #5a665e
    );

  color:white;
  font-weight:700;

  cursor:pointer;

  transition:
    transform .25s ease,
    opacity .25s ease,
    box-shadow .25s ease;
}

.primary-btn::before{
  content:"";

  position:absolute;
  top:0;
  left:-120%;

  width:100%;
  height:100%;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255,255,255,.25),
      transparent
    );

  transition:left .6s ease;
}

.primary-btn:hover::before{
  left:120%;
}

.primary-btn:hover{
  transform:translateY(-4px);

  box-shadow:
    0 15px 30px rgba(15,118,110,.3);
}

.primary-btn:disabled{
  opacity:.6;
  cursor:not-allowed;
}

/* ===== LAYOUT ===== */

.problems,
.solutions,
.microservices,
.benefits,
.process{
  max-width:1300px;
  margin:auto;
}

.problem-grid,
.solution-grid,
.micro-grid,
.benefit-grid,
.process-grid{
  display:grid;
  gap:2rem;
}

.problem-grid,
.solution-grid,
.micro-grid,
.benefit-grid{
  grid-template-columns:repeat(3,1fr);
}

.process-grid{
  grid-template-columns:repeat(4,1fr);
}

/* ===== CARDS ===== */

.problem-card,
.solution-card,
.micro-card,
.benefit-card,
.process-card{
  background:white;

  border-radius:24px;

  padding:2rem;

  border:1px solid #e2e8f0;

  opacity:0;
  transform:translateY(25px);

  animation:
    cardFade .8s ease forwards;

  transition:
    transform .25s ease,
    box-shadow .25s ease;
}

.problem-card:hover,
.solution-card:hover,
.micro-card:hover,
.benefit-card:hover,
.process-card:hover{
  transform:
    translateY(-8px)
    scale(1.01);

  box-shadow:
    0 18px 40px rgba(0,0,0,.08);
}

/* ===== STAGGER ===== */

.problem-card:nth-child(1),
.solution-card:nth-child(1),
.micro-card:nth-child(1),
.benefit-card:nth-child(1),
.process-card:nth-child(1){
  animation-delay:.1s;
}

.problem-card:nth-child(2),
.solution-card:nth-child(2),
.micro-card:nth-child(2),
.benefit-card:nth-child(2),
.process-card:nth-child(2){
  animation-delay:.2s;
}

.problem-card:nth-child(3),
.solution-card:nth-child(3),
.micro-card:nth-child(3),
.benefit-card:nth-child(3),
.process-card:nth-child(3){
  animation-delay:.3s;
}

.problem-card:nth-child(4),
.solution-card:nth-child(4),
.micro-card:nth-child(4),
.benefit-card:nth-child(4),
.process-card:nth-child(4){
  animation-delay:.4s;
}

.problem-card:nth-child(5),
.solution-card:nth-child(5),
.micro-card:nth-child(5),
.benefit-card:nth-child(5),
.process-card:nth-child(5){
  animation-delay:.5s;
}

.problem-card:nth-child(6),
.solution-card:nth-child(6),
.micro-card:nth-child(6),
.benefit-card:nth-child(6),
.process-card:nth-child(6){
  animation-delay:.6s;
}

/* ===== PROBLEMS ===== */

.problem-card{
  display:flex;
  align-items:center;
  gap:1rem;
}

.problem-icon{
  color:#ef4444;
  font-size:1.4rem;
}

/* ===== SOLUTIONS ===== */

.solution-icon{
  font-size:2rem;
  margin-bottom:1rem;
}

.solution-card h3{
  margin-bottom:1rem;
  color:#012719;
}

.solution-card p{
  margin-bottom:1.5rem;

  line-height:1.7;
  color:#475569;
}

.solution-card ul{
  padding-left:1rem;
}

.solution-card li{
  margin-bottom:.8rem;
}

/* ===== MICRO ===== */

.micro-card h3{
  margin-bottom:1rem;
}

.micro-card p{
  color:#475569;
  line-height:1.7;
}

.price{
  display:inline-block;
  margin-top:1.5rem;

  color:#0f766e;
  font-weight:700;
}

/* ===== BENEFITS ===== */

.benefit-card{
  display:flex;
  align-items:center;
  gap:1rem;
}

.benefit-card span{
  color:#0f766e;
  font-size:1.3rem;
}

/* ===== PROCESS ===== */

.step-number{
  display:inline-flex;

  width:60px;
  height:60px;

  align-items:center;
  justify-content:center;

  border-radius:50%;

  background:#ccfbf1;
  color:#0f766e;

  font-weight:800;
  margin-bottom:1.5rem;
}

/* ===== CTA ===== */

.cta-section{
  padding-bottom:6rem;
}

.cta-card{
  opacity:0;
  transform:translateY(40px);

  animation:
    fadeUp 1s ease .4s forwards;

  max-width:1100px;
  margin:auto;

  text-align:center;

  padding:5rem 2rem;

  border-radius:32px;

  background:
    linear-gradient(
      135deg,
      #012719,
      #0f766e
    );

  color:white;
}

.cta-label{
  display:inline-block;
  margin-bottom:1rem;

  opacity:.85;
}

.cta-card h2{
  font-size:clamp(2rem,5vw,4rem);
  margin-bottom:1rem;
}

.cta-card p{
  max-width:700px;

  margin:
    auto
    auto
    2rem;

  line-height:1.8;

  opacity:.9;
}

/* ===== KEYFRAMES ===== */

@keyframes fadeLeft{
  from{
    opacity:0;
    transform:translateX(-40px);
  }

  to{
    opacity:1;
    transform:translateX(0);
  }
}

@keyframes fadeRight{
  from{
    opacity:0;
    transform:
      translateX(40px)
      scale(.96);
  }

  to{
    opacity:1;
    transform:
      translateX(0)
      scale(1);
  }
}

@keyframes fadeUp{
  from{
    opacity:0;
    transform:translateY(30px);
  }

  to{
    opacity:1;
    transform:translateY(0);
  }
}

@keyframes cardFade{
  from{
    opacity:0;
    transform:translateY(25px);
  }

  to{
    opacity:1;
    transform:translateY(0);
  }
}

@keyframes imageZoom{
  from{
    transform:scale(1);
  }

  to{
    transform:scale(1.05);
  }
}

/* ===== RESPONSIVE ===== */

@media (max-width:1100px){

  .hero,
  .problem-grid,
  .solution-grid,
  .micro-grid,
  .benefit-grid,
  .process-grid{
    grid-template-columns:1fr;
  }

  .hero-image img{
    height:500px;
  }
}

@media (max-width:768px){

  section{
    padding:4rem 1rem;
  }

  .hero{
    min-height:auto;
  }

  .hero-title{
    font-size:2.8rem;
  }

  .hero-image img{
    height:350px;
  }
}
</style>
```
